package com.fantasytenis.web.theme

import org.jetbrains.compose.web.css.*

/**
 * Typography styles for the Fantasy Tennis web app.
 */
object Typography {

    object FontFamily {
        const val Primary = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        const val Mono = "'JetBrains Mono', 'Fira Code', 'Courier New', monospace"
    }

    object FontSize {
        val H1 = 2.5.cssRem
        val H2 = 2.cssRem
        val H3 = 1.75.cssRem
        val H4 = 1.5.cssRem
        val H5 = 1.25.cssRem
        val H6 = 1.cssRem
        val Body1 = 1.cssRem
        val Body2 = 0.875.cssRem
        val Caption = 0.75.cssRem
        val Overline = 0.625.cssRem
    }

    object FontWeight {
        val Light = 300
        val Regular = 400
        val Medium = 500
        val SemiBold = 600
        val Bold = 700
    }

    object LineHeight {
        val Tight = 1.25
        val Normal = 1.5
        val Relaxed = 1.75
    }
}

/**
 * Style builder extensions for common typography patterns.
 */
fun StyleScope.h1Style() {
    fontSize(Typography.FontSize.H1)
    fontWeight(Typography.FontWeight.Bold)
    lineHeight(Typography.LineHeight.Tight.toString())
    fontFamily(Typography.FontFamily.Primary)
}

fun StyleScope.h2Style() {
    fontSize(Typography.FontSize.H2)
    fontWeight(Typography.FontWeight.Bold)
    lineHeight(Typography.LineHeight.Tight.toString())
    fontFamily(Typography.FontFamily.Primary)
}

fun StyleScope.h3Style() {
    fontSize(Typography.FontSize.H3)
    fontWeight(Typography.FontWeight.SemiBold)
    lineHeight(Typography.LineHeight.Tight.toString())
    fontFamily(Typography.FontFamily.Primary)
}

fun StyleScope.bodyStyle() {
    fontSize(Typography.FontSize.Body1)
    fontWeight(Typography.FontWeight.Regular)
    lineHeight(Typography.LineHeight.Normal.toString())
    fontFamily(Typography.FontFamily.Primary)
}

fun StyleScope.captionStyle() {
    fontSize(Typography.FontSize.Caption)
    fontWeight(Typography.FontWeight.Regular)
    lineHeight(Typography.LineHeight.Normal.toString())
    fontFamily(Typography.FontFamily.Primary)
}

/**
 * CSS helper extensions for properties not directly available in Compose HTML.
 */
fun StyleScope.textTransform(value: String) {
    property("text-transform", value)
}

fun StyleScope.borderBottom(width: CSSNumeric, style: LineStyle, color: CSSColorValue) {
    property("border-bottom", "$width $style $color")
}

fun StyleScope.borderTop(width: CSSNumeric, style: LineStyle, color: CSSColorValue) {
    property("border-top", "$width $style $color")
}

fun StyleScope.borderLeft(width: CSSNumeric, style: LineStyle, color: CSSColorValue) {
    property("border-left", "$width $style $color")
}

fun StyleScope.borderRight(width: CSSNumeric, style: LineStyle, color: CSSColorValue) {
    property("border-right", "$width $style $color")
}

/**
 * Text truncation utilities for preventing overflow.
 */

/**
 * Truncates text to a single line with ellipsis.
 */
fun StyleScope.textTruncate() {
    overflow("hidden")
    property("text-overflow", "ellipsis")
    property("white-space", "nowrap")
}

/**
 * Truncates text to multiple lines with ellipsis.
 * Uses -webkit-line-clamp which has good browser support.
 *
 * @param lines Number of lines to show before truncating
 */
fun StyleScope.textClamp(lines: Int) {
    overflow("hidden")
    property("display", "-webkit-box")
    property("-webkit-line-clamp", lines.toString())
    property("-webkit-box-orient", "vertical")
}

/**
 * Prevents text from wrapping and allows horizontal overflow.
 */
fun StyleScope.textNoWrap() {
    property("white-space", "nowrap")
}

/**
 * Breaks long words to prevent overflow.
 */
fun StyleScope.wordBreak() {
    property("word-break", "break-word")
    property("overflow-wrap", "break-word")
}
